/**
 * Skills Page
 * Skills management with drag-and-drop reordering
 */

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Plus, AlertCircle, Filter, Languages } from "lucide-react";
import { useI18n } from "@/i18n";
import {
  SkillList,
  CreateSkillDialog,
  useSkills,
  useCreateSkill,
  useUpdateSkill,
  useDeleteSkill,
  useToggleSkill,
  useReorderSkills,
  useTranslateSkills,
} from "@/features/skills";
import type {
  Skill,
  CreateSkillRequest,
  UpdateSkillRequest,
} from "@/types/skills";

export function SkillsPage() {
  const { t } = useI18n();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [showDisabled, setShowDisabled] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<Skill | null>(null);

  // Queries and mutations
  const { data: skills = [], isLoading, error } = useSkills(showDisabled);
  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();
  const deleteMutation = useDeleteSkill();
  const toggleMutation = useToggleSkill();
  const reorderMutation = useReorderSkills();
  const translateMutation = useTranslateSkills();

  // Sort skills by display_row first, then display_order within each row
  const sortedSkills = [...skills].sort((a, b) => {
    const rowDiff = (a.display_row ?? 0) - (b.display_row ?? 0);
    if (rowDiff !== 0) return rowDiff;
    return a.display_order - b.display_order;
  });

  // Filter skills if needed
  const displaySkills = showDisabled
    ? sortedSkills
    : sortedSkills.filter((s) => s.enabled);

  const enabledCount = skills.filter((s) => s.enabled).length;

  const handleCreateClick = () => {
    setEditingSkill(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (skill: Skill) => {
    setEditingSkill(skill);
    setIsDialogOpen(true);
  };

  const handleDeleteClick = (skill: Skill) => {
    setDeleteConfirm(skill);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteMutation.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Failed to delete skill:", error);
    }
  };

  const handleToggle = async (skill: Skill) => {
    try {
      // Pass the opposite of current enabled state
      await toggleMutation.mutateAsync({
        skillId: skill.id,
        enabled: !skill.enabled,
      });
    } catch (error) {
      console.error("Failed to toggle skill:", error);
    }
  };

  const handleSubmit = async (
    data: CreateSkillRequest | UpdateSkillRequest,
  ) => {
    try {
      if (editingSkill) {
        await updateMutation.mutateAsync({
          skillId: editingSkill.id,
          data,
        });
      } else {
        await createMutation.mutateAsync(data as CreateSkillRequest);
      }
      setIsDialogOpen(false);
      setEditingSkill(null);
    } catch (error) {
      console.error("Failed to save skill:", error);
    }
  };

  const handleReorder = async (
    skillOrders: Array<{
      id: string;
      display_order: number;
      display_row: number;
    }>,
  ) => {
    try {
      await reorderMutation.mutateAsync(skillOrders);
    } catch (error) {
      console.error("Failed to reorder skills:", error);
    }
  };

  const handleTranslateAll = async () => {
    try {
      await translateMutation.mutateAsync({ targetLanguage: "en" });
    } catch (error) {
      console.error("Failed to translate skills:", error);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("skillsPage.pageTitle")}
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={handleTranslateAll}
              loading={translateMutation.isPending}
              disabled={translateMutation.isPending}
            >
              <Languages className="w-4 h-4 mr-2" />
              {translateMutation.isPending
                ? t("skillsPage.translating")
                : t("skillsPage.translateAll")}
            </Button>
            <Button variant="primary" onClick={handleCreateClick}>
              <Plus className="w-4 h-4 mr-2" />
              {t("skillsPage.createSkill")}
            </Button>
          </div>
        </div>
        <p className="text-gray-600">
          {t("skillsPage.pageDescription")}. {enabledCount} / {skills.length}{" "}
          {t("skillsPage.enabledCount").split("{")[0].trim()}
        </p>
      </div>

      {/* Filter Bar */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => setShowDisabled(!showDisabled)}
          className={`
              flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors
              ${
                showDisabled
                  ? "bg-primary-50 border-primary-500 text-primary-700"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }
            `}
        >
          <Filter className="w-4 h-4" />
          {showDisabled
            ? t("skillsPage.showAll")
            : t("skillsPage.showEnabledOnly")}
        </button>

        {reorderMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
            {t("skillsPage.savingOrder")}
          </div>
        )}
      </div>

      {/* Translate Success/Error */}
      {translateMutation.isSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {t("skillsPage.translateSuccess")} ({translateMutation.data.translated_count} skills)
        </div>
      )}
      {translateMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {t("skillsPage.translateError")}: {(translateMutation.error as Error).message}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-800 font-semibold mb-1">
                {t("skillsPage.errorLoading")}
              </h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-500">{t("skillsPage.loadingSkills")}</p>
          </div>
        </div>
      )}

      {/* Skills List */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <SkillList
            skills={displaySkills}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onToggle={handleToggle}
            onReorder={handleReorder}
          />
        </div>
      )}

      {/* Create/Edit Dialog */}
      <CreateSkillDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setEditingSkill(null);
        }}
        onSubmit={handleSubmit}
        skill={editingSkill}
        isSubmitting={isSubmitting}
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("skillsPage.deleteSkill")}
            </h3>
            <p className="text-gray-600 mb-4">
              {t("skillsPage.deleteConfirm")}{" "}
              <strong>{deleteConfirm.name}</strong>?{" "}
              {t("skillsPage.cannotUndo")}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                {t("common.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
